import { z } from "zod";
import {
  bookingModelSchema,
  classKindSchema,
  packageKindSchema,
  classLevelSchema,
  freeCancellationModeSchema,
  lateCancelRuleSchema,
} from "../enums.ts";
import { policySchema } from "../policy/types.ts";
import {
  bpsSchema,
  filsSchema,
  isoDateSchema,
  isoDateTimeSchema,
  localTimeSchema,
  timestampsSchema,
  uuidSchema,
} from "./common.ts";

export const groupSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    durationMin: z.number().int(),
    defaultCapacity: z.number().int().nullable(),
    bookingModel: bookingModelSchema,
    policyId: uuidSchema.nullable(),
    color: z.string().nullable(),
    kind: classKindSchema,
    /** The level's only coach. Null when it has none or more than one: read `staffUserIds`. */
    defaultStaffUserId: uuidSchema.nullable(),
    /** The coaches of the level, in the owner's order. */
    staffUserIds: z.array(uuidSchema),
    /**
     * Open to all: any participant books it with a package that names it, whatever their own group
     * (for example a training session for every level). Nobody is placed in it, and a package for
     * "any class" does not pay for it.
     */
    openToAll: z.boolean(),
    /** A short level on the class (migration 0074): a badge where members book. Null: none. Optional for older API builds. */
    level: classLevelSchema.nullable().optional(),
    sortOrder: z.number().int(),
    archivedAt: isoDateTimeSchema.nullable(),
  })
  .merge(timestampsSchema);
export type Group = z.infer<typeof groupSchema>;

export const createGroupRequestSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  durationMin: z.number().int().min(5).max(600),
  /**
   * Null or left out: no limit. A private group holds one person, or two for a duet
   * (`privateSeats`): any other value is one place.
   */
  defaultCapacity: z.number().int().min(1).max(500).nullable().default(null),
  bookingModel: bookingModelSchema,
  policyId: uuidSchema.optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Expected a hex colour like #1f8a70")
    .optional(),
  kind: classKindSchema.optional(),
  /** Older clients: one coach. Ignored when `staffUserIds` is in the request. */
  defaultStaffUserId: uuidSchema.nullable().optional(),
  /** The coaches of the level, in order. An empty list: no coach. */
  staffUserIds: z.array(uuidSchema).max(30).optional(),
  /** Open to all levels, with a package of its own. Group classes only. Left out: off. */
  openToAll: z.boolean().optional(),
  /** The level badge of the class (migration 0074). Null: no level. Left out: no change (none on a new class). */
  level: classLevelSchema.nullable().optional(),
  sortOrder: z.number().int().optional(),
});
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

export const updateGroupRequestSchema = createGroupRequestSchema
  .partial()
  .extend({ archived: z.boolean().optional() });
export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

/**
 * `POST /owner/businesses/:id/groups/:groupId/merge`: put other levels into this one (prototype
 * `teamna/prototypes/level-coaches.html`). Their weekly times, sessions, participants, coaches and
 * package links move here, then they are archived. Bookings stay on their sessions.
 */
export const mergeGroupsRequestSchema = z.object({
  groupIds: z.array(uuidSchema).min(1).max(20),
});
export type MergeGroupsRequest = z.infer<typeof mergeGroupsRequestSchema>;

export const mergeGroupsResponseSchema = z.object({
  group: groupSchema,
  merged: z.object({
    groups: z.number().int(),
    weeklyTimes: z.number().int(),
    sessions: z.number().int(),
    participants: z.number().int(),
    packages: z.number().int(),
  }),
});
export type MergeGroupsResponse = z.infer<typeof mergeGroupsResponseSchema>;

/** A stored `policies` row. The engine only reads the subset in `policySchema`. */
export const policyRecordSchema = policySchema
  .extend({
    id: uuidSchema,
    businessId: uuidSchema,
    name: z.string(),
    /** The no-show and late-cancel fee in fils, null for no fee (I2, migration 0078). */
    noShowFeeFils: z.number().int().nullable().default(null),
    lateCancelFeeFils: z.number().int().nullable().default(null),
  })
  .merge(timestampsSchema);
export type PolicyRecord = z.infer<typeof policyRecordSchema>;

export const createPolicyRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    cancelCutoffHours: z.number().int().min(0).max(720),
    lateCancelRule: lateCancelRuleSchema,
    lateCancelPartialBps: bpsSchema.nullable().default(null),
    rescheduleLimitPerBooking: z.number().int().min(0).max(50).nullable().default(null),
    rescheduleLimitPerMonth: z.number().int().min(0).max(200).nullable().default(null),
    minBookAheadMin: z.number().int().min(0).max(10_080).default(0),
    maxBookAheadDays: z.number().int().min(1).max(365).default(56),
  })
  .refine(
    (value) => value.lateCancelRule !== "partial" || value.lateCancelPartialBps !== null,
    { message: "A partial late-cancel rule needs lateCancelPartialBps", path: ["lateCancelPartialBps"] },
  );
export type CreatePolicyRequest = z.infer<typeof createPolicyRequestSchema>;

/** Partial update. The `partial` rule still needs its share, checked in the service. */
export const updatePolicyRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    cancelCutoffHours: z.number().int().min(0).max(720),
    lateCancelRule: lateCancelRuleSchema,
    lateCancelPartialBps: bpsSchema.nullable(),
    rescheduleLimitPerBooking: z.number().int().min(0).max(50).nullable(),
    rescheduleLimitPerMonth: z.number().int().min(0).max(200).nullable(),
    minBookAheadMin: z.number().int().min(0).max(10_080),
    maxBookAheadDays: z.number().int().min(1).max(365),
  })
  .partial();
export type UpdatePolicyRequest = z.infer<typeof updatePolicyRequestSchema>;

export const scheduleTemplateSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    groupId: uuidSchema,
    weekday: z.number().int().min(0).max(6),
    startTime: localTimeSchema,
    capacity: z.number().int().nullable(),
    validFrom: isoDateSchema,
    validUntil: isoDateSchema.nullable(),
    active: z.boolean(),
    staffUserId: uuidSchema.nullable(),
    durationMin: z.number().int().nullable(),
  })
  .merge(timestampsSchema);
export type ScheduleTemplate = z.infer<typeof scheduleTemplateSchema>;

export const createScheduleTemplateRequestSchema = z.object({
  groupId: uuidSchema,
  weekday: z.number().int().min(0).max(6),
  startTime: localTimeSchema,
  capacity: z.number().int().min(1).max(500).nullable(),
  validFrom: isoDateSchema,
  validUntil: isoDateSchema.nullable().default(null),
  staffUserId: uuidSchema.nullable().optional(),
  durationMin: z.number().int().min(5).max(600).nullable().optional(),
});
export type CreateScheduleTemplateRequest = z.infer<typeof createScheduleTemplateRequestSchema>;

export const updateScheduleTemplateRequestSchema = createScheduleTemplateRequestSchema
  .partial()
  .extend({ active: z.boolean().optional() });
export type UpdateScheduleTemplateRequest = z.infer<typeof updateScheduleTemplateRequestSchema>;

export const packageProductSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    name: z.string(),
    credits: z.number().int(),
    priceFils: filsSchema,
    validityDays: z.number().int(),
    /** Null means the credits work for any group in the business. */
    groupIds: z.array(uuidSchema).nullable(),
    /**
     * How many cancellations return the session to the member's balance rather than using it up.
     * Null means unlimited, which is the behaviour of every package sold before this existed.
     */
    freeCancellations: z.number().int().min(0).nullable(),
    /**
     * Which cancellations spend `freeCancellations`. Defaults so clients written before the field
     * keep parsing.
     */
    freeCancellationMode: freeCancellationModeSchema.default("any_cancel"),
    active: z.boolean(),
    kind: classKindSchema,
    autoRenew: z.boolean(),
    /**
     * An intro offer (migration 0070): only a new client can buy it, one time. The member shop
     * lists it first, and only for a new client. Defaults so clients written before it keep parsing.
     */
    newClientsOnly: z.boolean().default(false),
    /**
     * `pack` or `membership` (migration 0079): a monthly membership with a class cap. For a
     * membership `credits` is the cap of a month and `priceFils` the price of a month. Defaults so
     * clients written before it keep parsing.
     */
    packageKind: packageKindSchema.optional(),
    /** A membership with no cap (`credits` is 0). */
    unlimited: z.boolean().optional(),
  })
  .merge(timestampsSchema);
export type PackageProduct = z.infer<typeof packageProductSchema>;

const packageProductFieldsSchema = z.object({
  name: z.string().min(1).max(120),
  /** The sessions of a pack, or the classes of a month of a membership. 0 only for an unlimited membership. */
  credits: z.number().int().min(0).max(1000),
  priceFils: filsSchema.min(0),
  validityDays: z.number().int().min(1).max(730),
  groupIds: z.array(uuidSchema).nullable().default(null),
  /** Left out or null means unlimited, which is the right default for a business that has no rule. */
  freeCancellations: z.number().int().min(0).max(1000).nullable().default(null),
  /** Left out means `any_cancel`, the behaviour before the mode existed. */
  freeCancellationMode: freeCancellationModeSchema.default("any_cancel"),
  kind: classKindSchema.default("group"),
  autoRenew: z.boolean().default(true),
  /** An intro offer: only a new client can buy it, one time. It never renews, so the API sets `autoRenew` false. */
  newClientsOnly: z.boolean().default(false),
  /** `membership`: a monthly membership with a class cap (M2). It renews each month, is never an intro offer. */
  packageKind: packageKindSchema.default("pack"),
  /** A membership with no cap. Send `credits: 0` with it. */
  unlimited: z.boolean().default(false),
});

export const createPackageProductRequestSchema = packageProductFieldsSchema
  .extend({
    /** The package this one is a copy of (Duplicate on the packages page). Only for analytics: no field is read from it. */
    copiedFrom: uuidSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.credits < 1 && !(value.packageKind === "membership" && value.unlimited)) {
      ctx.addIssue({ code: "too_small", minimum: 1, type: "number", inclusive: true, path: ["credits"], message: "Give 1 session or more." });
    }
  });
export type CreatePackageProductRequest = z.infer<typeof createPackageProductRequestSchema>;

export const updatePackageProductRequestSchema = packageProductFieldsSchema
  .partial()
  .extend({ active: z.boolean().optional() });
export type UpdatePackageProductRequest = z.infer<typeof updatePackageProductRequestSchema>;
